import { useCallback, useState } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { insertErefIntoCache } from "helpers/quotationCache.helper"
import AnnotationService from "services/annotations.service"
import type { useCreateEref } from "hooks/api/quotation/quotations.hook"
import type { TEref } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TPaneBinding } from "../core/viewerPanes"
import type { TViewerTool } from "../core/viewerTools"
import { useAreaCreate, type TAreaCreateSession, type TAreaDraft } from "./useAreaCreate"

export type TErefDraftArea = TAreaDraft
export type TErefCreateSession = TAreaCreateSession

export type ErefCreateContext = {
	/**
	 * Orchestrator-owned mirror of the live session — owned there, like the connect-objects one,
	 * because the canvas and sidebar handlers that must stay inert while the mode is open are
	 * declared before this hook runs.
	 */
	createSessionRef: MutableRefObject<TErefCreateSession | null>
	/** Plain (non-React) draft box + its four corner handles, from createOverlayContainers. */
	erefDraftRectRef: MutableRefObject<{ element: HTMLElement; handles: HTMLElement[] } | null>
	/** `.App` element — the origin the box's app-relative coordinates are measured against. */
	appContainerRef: MutableRefObject<HTMLElement | null>
	/** The live viewer panes, re-resolved on every call (they are replaced on a layout change). */
	getViewerPanes: () => TPaneBinding[]
	/** Bumped by split-screen enter/exit; re-binds the canvas listeners onto the new panes. */
	layoutEpoch: number
	numericQuotationIdRef: MutableRefObject<number | undefined>
	createErefAsync: ReturnType<typeof useCreateEref>["mutateAsync"]
	queryClient: QueryClient
	/** Post-create hand-off to the orchestrator: select the new eref and open its sidebar. */
	onErefCreated: (eref: TEref) => void
	/** The shared tool command (useCanvasTools) — the mode puts every pane back into select. */
	onToolChange: (tool: TViewerTool) => void
	clearSelectionToolbar: () => void
	success: (text: string) => void
	formatError: (error: unknown) => void
}

/**
 * "Add elevation reference" mode (REQ-292), opened from the header's + Add menu. It takes over the
 * document area: the right sidebar asks the user to drag out the elevation area, the drag paints a
 * blue box with corner handles, and Create posts the box + the elevation number to
 * `POST /quotations/{id}/eref/`.
 *
 * Three things are deliberate:
 *
 *   • The draft box is anchored to the PAGE, not the viewport. Its truth is the backend rect in
 *     `area`; the on-screen box is re-derived from that whenever the pane scrolls, zooms or
 *     re-lays-out, so scrolling away and back doesn't leave the outline stranded mid-air.
 *   • `beginAreaDrawing` / `endAreaDrawing` bracket the mode so a drag that starts over an existing
 *     highlight draws instead of moving it (REQ-350). Every exit path — cancel, a successful
 *     create, leaving editor mode, closing the modal, unmount — must end it.
 *   • The response is applied straight to the cache (`insertErefIntoCache`) and the canvas
 *     (`addErefAnnotation`), so the new elevation shows up in the left sidebar and on the drawing
 *     without a refetch — falling back to one only when the cache had no page to put it on.
 */
export const useErefCreate = (ctx: ErefCreateContext) => {
	const {
		createSessionRef,
		erefDraftRectRef,
		appContainerRef,
		getViewerPanes,
		layoutEpoch,
		numericQuotationIdRef,
		createErefAsync,
		queryClient,
		onErefCreated,
		onToolChange,
		clearSelectionToolbar,
		success,
		formatError,
	} = ctx

	const { createSession, startCreateArea, cancelCreateArea } = useAreaCreate({
		createSessionRef,
		draftRectRef: erefDraftRectRef,
		appContainerRef,
		getViewerPanes,
		layoutEpoch,
		onToolChange,
		clearSelectionToolbar,
	})
	const [isCreatePending, setIsCreatePending] = useState(false)

	const runCreateEref = useCallback(
		async ({ elevationNumber, pageName }: { elevationNumber: string; pageName: string }) => {
			const qid = numericQuotationIdRef.current
			const area = createSessionRef.current?.area
			if (qid == null || Number.isNaN(qid) || !area) return
			setIsCreatePending(true)
			try {
				const created = (
					await createErefAsync({
						quotationId: qid,
						body: {
							elevation_number: elevationNumber,
							page_name: pageName,
							page_number: area.pageNumber,
							x0: area.coordinates.x0,
							y0: area.coordinates.y0,
							x1: area.coordinates.x1,
							y1: area.coordinates.y1,
						},
					})
				)?.data
				if (!created) return
				let isInCache = false
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], (cached) => {
					const next = insertErefIntoCache(cached, created)
					// The helper returns the SAME reference when it found no home for the eref, which is
					// how a no-op is detected here.
					isInCache = next !== cached
					return next
				})
				// `related_objects` only carries pages that have detections (see the note in
				// AnnotationService.renderAnnotations), so an elevation drawn on an untouched page has no
				// page entry to be appended to and the splice above silently does nothing. Left at that,
				// the new eref would be missing from the left sidebar and invisible to
				// `suggestNextElevationNumber`, which would keep offering "1" for that page until a second
				// create 400s on the duplicate (page_number, elevation_number) pair. The refetch is heavy,
				// hence only on this path — and it cannot double-paint the canvas, because annotations are
				// rendered from `documentLoaded`, not from the query data.
				if (!isInCache) queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
				AnnotationService.addErefAnnotation(created)
				// Tear the mode down BEFORE handing over: the hand-off opens the new eref's own sidebar,
				// which would otherwise be fighting the create panel for the right panel.
				cancelCreateArea()
				onErefCreated(created)
				success("Elevation reference created")
			} catch (e) {
				// The area and the panel stay exactly as they were so the user can fix the elevation
				// number (a duplicate number / duplicate coordinates both come back as a 400) and retry.
				formatError(e)
			} finally {
				setIsCreatePending(false)
			}
		},
		[
			numericQuotationIdRef,
			createSessionRef,
			createErefAsync,
			queryClient,
			cancelCreateArea,
			onErefCreated,
			success,
			formatError,
		],
	)

	return {
		createSession,
		isCreatePending,
		startCreateEref: startCreateArea,
		cancelCreateEref: cancelCreateArea,
		runCreateEref,
	}
}
